import React, { useMemo, useState } from 'react';
import friendlyError from '../../../utils/friendlyError';
import styled from 'styled-components';
import {
  FaRobot,
  FaCopy,
  FaSave,
  FaSyncAlt,
  FaCheck,
  FaBolt,
  FaThList,
} from 'react-icons/fa';

import {
  TechSection,
  TechInput,
  TechTextarea,
  TechSelect,
  TechButton,
  TechButtonGhost,
  StatusPill,
  Empty,
  Loading,
  ErrorBox,
  borderSoft,
  panelDeep,
  mono,
  accent,
} from '../../../styles/techComponents';

import {
  generateMessage,
  saveMaterial,
} from '../../../services/messagingService';

import {
  TEMPLATES,
  TYPE_OPTIONS,
  CHANNEL_OPTIONS,
  AUDIENCE_OPTIONS,
  TONE_OPTIONS,
  channelCharLimit,
} from '../templates';

/* ---------- estilos locales ---------- */

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  font-family: ${mono};
  font-size: 0.62rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--text-disabled);
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ $cols }) => $cols || 2}, 1fr);
  gap: 0.85rem;

  @media (max-width: 720px) { grid-template-columns: 1fr; }
`;

const TemplateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.6rem;
  margin-bottom: 1rem;
`;

const TemplateCard = styled.button`
  position: relative;
  text-align: left;
  background: ${({ $active }) => ($active ? 'rgba(0, 180, 216, 0.08)' : panelDeep)};
  border: 1px solid ${({ $active }) => ($active ? 'rgba(0, 180, 216, 0.5)' : borderSoft)};
  border-radius: 5px;
  padding: 0.7rem 0.85rem;
  cursor: pointer;
  font-family: inherit;
  color: var(--text-secondary);
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: rgba(0, 180, 216, 0.4);
    background: rgba(0, 180, 216, 0.04);
  }

  .head {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.3rem;
  }
  .icon {
    font-size: 0.95rem;
    flex-shrink: 0;
  }
  .name {
    font-size: 0.82rem;
    color: var(--text-primary);
    font-weight: 600;
    line-height: 1.3;
    word-break: break-word;
  }
  .meta {
    font-family: ${mono};
    font-size: 0.6rem;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${accent};
    margin-bottom: 0.3rem;
  }
  .topic {
    font-size: 0.78rem;
    color: var(--text-disabled);
    line-height: 1.4;
    word-break: break-word;
  }
`;

const ResultBlock = styled.article`
  position: relative;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.012), transparent 35%),
    var(--color-tertiary);
  border: 1px solid ${borderSoft};
  border-left: 3px solid ${accent};
  border-radius: 6px;
  padding: 1rem 1.15rem;
  margin-bottom: 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 0.7rem;

  &::before, &::after {
    content: '';
    position: absolute;
    width: 10px; height: 10px;
    border-color: ${accent};
    border-style: solid;
    border-width: 0;
    pointer-events: none;
  }
  &::before { top: -1px; left: -1px; border-top-width: 1px; border-left-width: 1px; }
  &::after { bottom: -1px; right: -1px; border-bottom-width: 1px; border-right-width: 1px; }
`;

const ResultHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.6rem;
  font-family: ${mono};
  font-size: 0.65rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--text-disabled);

  .label { color: ${accent}; }
  .ts { margin-left: auto; }
`;

const ResultBody = styled.pre`
  margin: 0;
  white-space: pre-wrap;
  font-family: inherit;
  font-size: 0.92rem;
  line-height: 1.55;
  color: var(--text-secondary);
  word-break: break-word;
`;

const CharMeter = styled.span`
  font-family: ${mono};
  font-size: 0.62rem;
  letter-spacing: 0.16em;
  color: ${({ $over }) => ($over ? 'var(--alert-error)' : 'var(--text-disabled)')};
`;

const ContextSummary = styled.div`
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
  font-family: ${mono};
  font-size: 0.6rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--text-disabled);

  span {
    border: 1px solid ${borderSoft};
    border-radius: 3px;
    padding: 1px 6px;
  }
  strong { color: ${accent}; margin-left: 4px; }
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const Toggle = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-family: ${mono};
  font-size: 0.66rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--text-disabled);
  cursor: pointer;
  user-select: none;

  input { accent-color: ${accent}; }
`;

/* ---------- componente ---------- */

const EMPTY = {
  topic: '',
  details: '',
  type: 'speech',
  channel: '',
  audience: 'all',
  tone: 'formal',
  variants: 1,
  use_context: true,
};

const CopilotTab = () => {
  const [form, setForm] = useState(EMPTY);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [savedMap, setSavedMap] = useState({}); // index → savedId
  const [showTemplates, setShowTemplates] = useState(true);

  const handle = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const applyTemplate = (tpl) => {
    setActiveTemplate(tpl.id);
    setForm({
      topic: tpl.topic,
      details: tpl.details,
      type: tpl.type,
      channel: tpl.channel || '',
      audience: tpl.audience,
      tone: tpl.tone,
      variants: 1,
      use_context: true,
    });
    setShowTemplates(false);
  };

  const reset = () => {
    setForm(EMPTY);
    setActiveTemplate(null);
    setResults(null);
    setError(null);
    setSavedMap({});
  };

  const generate = async () => {
    if (!form.topic.trim()) {
      setError('Ingresá un tema antes de generar.');
      return;
    }
    setError(null);
    setGenerating(true);
    setResults(null);
    setSavedMap({});
    try {
      const data = await generateMessage({
        topic: form.topic,
        details: form.details,
        type: form.type,
        channel: form.type === 'social' && form.channel ? form.channel : null,
        audience: form.audience,
        tone: form.tone,
        variants: form.variants,
        use_context: form.use_context,
      });
      setResults(data);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setGenerating(false);
    }
  };

  const copy = (text, idx) => {
    navigator.clipboard.writeText(text).then(() => {
      // marcar copiado temporalmente
      setSavedMap((m) => ({ ...m, [`copy-${idx}`]: Date.now() }));
      setTimeout(() => {
        setSavedMap((m) => {
          const c = { ...m };
          delete c[`copy-${idx}`];
          return c;
        });
      }, 1500);
    });
  };

  const persist = async (text, idx) => {
    if (savedMap[idx]) return;
    const titleBase = form.topic.slice(0, 100);
    const variantSuffix = results?.results?.length > 1 ? ` · v${idx + 1}` : '';
    try {
      const m = await saveMaterial({
        title: titleBase + variantSuffix,
        content: text,
        type: form.type,
        channel: form.type === 'social' && form.channel ? form.channel : null,
        audience: form.audience,
        tone: form.tone,
        topic: form.topic.slice(0, 80),
        tags: [form.type, form.audience, form.tone].filter(Boolean),
        source_prompt: form.details || null,
        source_params: {
          type: form.type, channel: form.channel || null,
          audience: form.audience, tone: form.tone, variant: idx + 1,
        },
        source: 'ai',
      });
      setSavedMap((s) => ({ ...s, [idx]: m.id }));
    } catch (err) {
      alert('No se pudo guardar: ' + (friendlyError(err)));
    }
  };

  const charLimit = useMemo(() => {
    if (form.type !== 'social' || !form.channel) return null;
    return channelCharLimit(form.channel);
  }, [form.type, form.channel]);

  return (
    <>
      {showTemplates ? (
        <TechSection
          title="Plantillas de partida"
          action={
            <TechButtonGhost type="button" onClick={() => setShowTemplates(false)}>
              <FaThList /> Ocultar plantillas
            </TechButtonGhost>
          }
        >
          <TemplateGrid>
            {TEMPLATES.map((tpl) => (
              <TemplateCard
                key={tpl.id}
                $active={activeTemplate === tpl.id}
                onClick={() => applyTemplate(tpl)}
                type="button"
              >
                <div className="head">
                  <span className="icon">{tpl.icon}</span>
                  <span className="name">{tpl.title}</span>
                </div>
                <div className="meta">
                  {tpl.type}
                  {tpl.channel ? ` · ${tpl.channel}` : ''}
                  {' · '}{tpl.audience}
                </div>
                <div className="topic">{tpl.topic}</div>
              </TemplateCard>
            ))}
          </TemplateGrid>
        </TechSection>
      ) : (
        <div style={{ marginBottom: '0.85rem' }}>
          <TechButtonGhost type="button" onClick={() => setShowTemplates(true)}>
            <FaThList /> Ver plantillas
          </TechButtonGhost>
        </div>
      )}

      <TechSection
        title="Brief para la IA"
        action={
          <Toggle title="Si está activo, la IA lee competidores, noticias recolectadas, hallazgos y objetivos">
            <input
              type="checkbox"
              checked={form.use_context}
              onChange={(e) => handle('use_context', e.target.checked)}
            />
            Usar contexto en vivo
          </Toggle>
        }
      >
        <Field style={{ marginBottom: '0.85rem' }}>
          Tema
          <TechInput
            value={form.topic}
            onChange={(e) => handle('topic', e.target.value)}
            placeholder="Ej: Presentación del proyecto de ordenanza de protección de humedales"
          />
        </Field>

        <Field style={{ marginBottom: '0.85rem' }}>
          Detalles / instrucciones específicas
          <TechTextarea
            value={form.details}
            onChange={(e) => handle('details', e.target.value)}
            rows={3}
            placeholder="Datos puntuales, tono específico, citas que querés incluir, etc."
          />
        </Field>

        <Row $cols={3} style={{ marginBottom: '0.85rem' }}>
          <Field>
            Tipo
            <TechSelect value={form.type} onChange={(e) => handle('type', e.target.value)}>
              {TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </TechSelect>
          </Field>
          <Field>
            Audiencia
            <TechSelect value={form.audience} onChange={(e) => handle('audience', e.target.value)}>
              {AUDIENCE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </TechSelect>
          </Field>
          <Field>
            Tono
            <TechSelect value={form.tone} onChange={(e) => handle('tone', e.target.value)}>
              {TONE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </TechSelect>
          </Field>
        </Row>

        <Row $cols={2} style={{ marginBottom: '0.85rem' }}>
          {form.type === 'social' && (
            <Field>
              Canal
              <TechSelect value={form.channel} onChange={(e) => handle('channel', e.target.value)}>
                {CHANNEL_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </TechSelect>
            </Field>
          )}
          <Field>
            Variantes a generar
            <TechSelect value={form.variants} onChange={(e) => handle('variants', Number(e.target.value))}>
              <option value={1}>1 variante</option>
              <option value={2}>2 variantes</option>
              <option value={3}>3 variantes</option>
            </TechSelect>
          </Field>
        </Row>

        <Actions>
          <TechButton type="button" onClick={generate} disabled={generating}>
            <FaBolt /> {generating ? 'Generando…' : 'Generar contenido'}
          </TechButton>
          <TechButtonGhost type="button" onClick={reset} disabled={generating}>
            <FaSyncAlt /> Reset
          </TechButtonGhost>
        </Actions>
      </TechSection>

      <TechSection title={results ? `Resultado · ${results.results?.length || 0} variante(s)` : 'Resultado'}>
        {generating && <Loading>IA leyendo plataforma + redactando…</Loading>}
        {!generating && error && <ErrorBox>No se pudo generar · {error}</ErrorBox>}
        {!generating && !error && !results && (
          <Empty>Cargá un tema y disparale a "Generar contenido".</Empty>
        )}

        {results && (
          <>
            {results.context_used && results.context_summary && (
              <ContextSummary style={{ marginBottom: '0.85rem' }}>
                <span>contexto usado:</span>
                <span>competidores <strong>{results.context_summary.competidores}</strong></span>
                <span>noticias <strong>{results.context_summary.noticias}</strong></span>
                <span>insights <strong>{results.context_summary.insights}</strong></span>
                <span>objetivos <strong>{results.context_summary.objetivos}</strong></span>
              </ContextSummary>
            )}

            {results.results.map((text, idx) => {
              const len = text.length;
              const over = charLimit && len > charLimit;
              const wasSaved = savedMap[idx];
              const wasCopied = !!savedMap[`copy-${idx}`];

              return (
                <ResultBlock key={idx}>
                  <ResultHeader>
                    <span className="label">
                      <FaRobot /> Variante {idx + 1}
                    </span>
                    {charLimit && (
                      <CharMeter $over={over}>
                        {len} / {charLimit} chars{over ? ' · excede límite' : ''}
                      </CharMeter>
                    )}
                    <span className="ts">{form.type} · {form.audience} · {form.tone}</span>
                  </ResultHeader>

                  <ResultBody>{text}</ResultBody>

                  <Actions>
                    <TechButtonGhost type="button" onClick={() => copy(text, idx)}>
                      {wasCopied ? <><FaCheck /> Copiado</> : <><FaCopy /> Copiar</>}
                    </TechButtonGhost>
                    <TechButton type="button" onClick={() => persist(text, idx)} disabled={!!wasSaved}>
                      {wasSaved ? <><FaCheck /> Guardado · #{wasSaved}</> : <><FaSave /> Guardar en biblioteca</>}
                    </TechButton>
                  </Actions>
                </ResultBlock>
              );
            })}
          </>
        )}
      </TechSection>
    </>
  );
};

export default CopilotTab;
